/**
 * Helpers to get view position and normal information in a screen space pass.
 */
import * as THREE from "three";
import type { DepthNormalPass } from "./DepthNormalPass";
import { normalPackingHelpersGLSL } from "./normalPackingHelpersGLSL";

export interface ScreenSpaceViewPositionDefines {
  DEPTH_TEX_CONTAINS_VIEWZ: number; // eslint-disable-line @typescript-eslint/naming-convention
  DEPTH_PACKING: number; // eslint-disable-line @typescript-eslint/naming-convention
  PERSPECTIVE_CAMERA: number; // eslint-disable-line @typescript-eslint/naming-convention
}

export const screenSpaceViewPositionDefineDefaults: ScreenSpaceViewPositionDefines = {
  DEPTH_TEX_CONTAINS_VIEWZ: 0,
  DEPTH_PACKING: 0,
  PERSPECTIVE_CAMERA: 1,
};

export interface ScreenSpaceViewPositionUniforms extends Record<string, THREE.IUniform> {
  tDepth: THREE.IUniform;
  cameraNear: THREE.IUniform;
  cameraFar: THREE.IUniform;
  cameraProjectionMatrix: THREE.IUniform;
  cameraInverseProjectionMatrix: THREE.IUniform;
}

export const screenSpaceViewPositionUniformDefaults: ScreenSpaceViewPositionUniforms = {
  tDepth: { value: null },
  cameraNear: { value: 1.0 },
  cameraFar: { value: 1000.0 },
  cameraProjectionMatrix: { value: new THREE.Matrix4() },
  cameraInverseProjectionMatrix: { value: new THREE.Matrix4() },
};

export interface ViewPositionOptions {
  forceRawDepthBuffer?: boolean; // Using the raw depth buffer can increase precision since the depth values don't go through intermediate storage.
  skipSettingDepthBuffer?: boolean;
  overrideProjectionMatrix?: THREE.Matrix4;
}

export function screenSpaceViewPositionPrepareShader(
  material: THREE.ShaderMaterial,
  camera: THREE.Camera,
  depthNormalPass: DepthNormalPass,
  options: ViewPositionOptions = {
    forceRawDepthBuffer: false,
    skipSettingDepthBuffer: false,
    overrideProjectionMatrix: undefined,
  },
) {
  const defines: ScreenSpaceViewPositionDefines = material.defines as any;
  const uniforms: ScreenSpaceViewPositionUniforms = material.uniforms as any;
  const oldDefines = { ...defines };
  defines.PERSPECTIVE_CAMERA = (camera as THREE.PerspectiveCamera).isPerspectiveCamera ? 1 : 0;
  defines.DEPTH_PACKING = depthNormalPass.isDepthRGBAPacked() ? 1 : 0;

  const useViewZBuffer = options.forceRawDepthBuffer ? false : depthNormalPass.params.enableViewZConversion;
  defines.DEPTH_TEX_CONTAINS_VIEWZ = useViewZBuffer ? 1 : 0;
  const depthTexture = useViewZBuffer ? depthNormalPass.viewZTexture : depthNormalPass.depthTexture;
  if (
    defines.PERSPECTIVE_CAMERA !== oldDefines.PERSPECTIVE_CAMERA ||
    defines.DEPTH_PACKING !== oldDefines.DEPTH_PACKING ||
    defines.DEPTH_TEX_CONTAINS_VIEWZ !== oldDefines.DEPTH_TEX_CONTAINS_VIEWZ
  ) {
    material.needsUpdate = true;
  }

  if (!options.skipSettingDepthBuffer) {
    uniforms.tDepth.value = depthTexture;
  }

  const projectionMatrix = options.overrideProjectionMatrix ?? depthNormalPass.paddedProjectionMatrix;
  uniforms.cameraNear.value = (camera as THREE.PerspectiveCamera).near;
  uniforms.cameraFar.value = (camera as THREE.PerspectiveCamera).far;
  uniforms.cameraProjectionMatrix.value.copy(projectionMatrix);
  uniforms.cameraInverseProjectionMatrix.value.copy(projectionMatrix).invert();
}

export const screenSpaceViewPositionGLSL = `

uniform float cameraNear;
uniform float cameraFar;
uniform mat4 cameraProjectionMatrix;
uniform mat4 cameraInverseProjectionMatrix;

#if PERSPECTIVE_CAMERA
#define POINT_TO_CAMERA_DIRECTION(viewPosition) normalize(-viewPosition)
#else
#define POINT_TO_CAMERA_DIRECTION(viewPosition) vec3(0, 0, 1)
#endif

#if PERSPECTIVE_CAMERA == 1
#define INIT_Y_RELATIVE_RADIUS_FROM_WORLD_SPACE(radius, worldSpaceScale, viewZ, cameraProjectionMatrix) float radius = worldSpaceScale / abs(viewZ)
#else
#define INIT_Y_RELATIVE_RADIUS_FROM_WORLD_SPACE(radius, worldSpaceScale, viewZ, cameraProjectionMatrix) float radius = worldSpaceScale * cameraProjectionMatrix[1][1] * 0.5
#endif

#if !defined(GET_DEPTH)
uniform sampler2D tDepth;

#if DEPTH_TEX_CONTAINS_VIEWZ == 1 || (DEPTH_PACKING == 0)
#define GET_DEPTH(tDepthA, screenPosition) texture2D(tDepthA, screenPosition).x
#else
#define GET_DEPTH(tDepthA, screenPosition) unpackRGBAToDepth(texture2D(tDepthA, screenPosition))
#endif
#endif

#if DEPTH_TEX_CONTAINS_VIEWZ == 1
#define IS_DEPTH_OUT_OF_BOUNDS(depth) (depth <= -cameraFar * (1.0 - EPSILON))
#define IS_ANY_DEPTH_OUT_OF_BOUNDS(depth1, depth2) (min(depth1, depth2) <= -cameraFar * (1.0 - EPSILON))
#else
#define IS_DEPTH_OUT_OF_BOUNDS(depth) (depth >= 1.0 - EPSILON)
#define IS_ANY_DEPTH_OUT_OF_BOUNDS(depth1, depth2) (max(depth1, depth2) >= 1.0 - EPSILON)
#endif

#if DEPTH_TEX_CONTAINS_VIEWZ == 0
float getViewZ(const in float depth) {
  #if PERSPECTIVE_CAMERA == 1
  return perspectiveDepthToViewZ(depth, cameraNear, cameraFar);
  #else
  return orthographicDepthToViewZ(depth, cameraNear, cameraFar);
  #endif
}
#else
#define getViewZ(depth) depth
#endif

// screenPosition in the 0 to 1 range
vec3 getViewPosition(const in vec2 screenPosition, const in float viewZ) {
  #if PERSPECTIVE_CAMERA == 1
  // Optimized formula taken from SAO paper.
  vec2 xy = viewZ * vec2(
    (1.0 - cameraProjectionMatrix[2][0] - 2.0 * screenPosition.x) / cameraProjectionMatrix[0][0],
    (1.0 - cameraProjectionMatrix[2][1] - 2.0 * screenPosition.y) / cameraProjectionMatrix[1][1]);
  return vec3(xy, viewZ);
  #else
  float depth = viewZToOrthographicDepth(viewZ, cameraNear, cameraFar);
  vec4 clipPosition = vec4((vec3(screenPosition, depth) - 0.5) * 2.0, 1.0);
  return (cameraInverseProjectionMatrix * clipPosition).xyz;
  #endif
}

#if DEPTH_TEX_CONTAINS_VIEWZ == 0
// Alternative getViewPosition to get a bit more precision by providing the raw depth value
vec3 getViewPosition(const in vec2 screenPosition, const in float depth, const in float viewZ) {
  float clipW = cameraProjectionMatrix[2][3] * viewZ + cameraProjectionMatrix[3][3];
  vec4 clipPosition = vec4((vec3(screenPosition, depth) - 0.5) * 2.0, 1.0);
  clipPosition *= clipW; // unprojection.
  return (cameraInverseProjectionMatrix * clipPosition).xyz;
}
#endif

`;

export interface ScreenSpaceViewNormalDefines {
  NORMAL_TEXTURE: number; // eslint-disable-line @typescript-eslint/naming-convention
  NORMAL_PACKING: number; // eslint-disable-line @typescript-eslint/naming-convention
}

export const screenSpaceViewNormalDefineDefaults = {
  NORMAL_TEXTURE: 1,
  NORMAL_PACKING: 1,
};

export interface ScreenSpaceViewNormalUniforms {
  [key: string]: THREE.IUniform;
  tNormal: THREE.IUniform;
}

export const screenSpaceViewNormalUniformDefaults = {
  tNormal: { value: null },
};

export function screenSpaceViewNormalPrepareShader(material: THREE.ShaderMaterial, depthNormalPass: DepthNormalPass) {
  const uniforms: ScreenSpaceViewNormalUniforms = material.uniforms as any;
  uniforms.tNormal.value = depthNormalPass.normalTexture;
}

export const screenSpaceViewNormalGLSL = `

#if NORMAL_TEXTURE == 1

#if NORMAL_PACKING == 1
${normalPackingHelpersGLSL}
#endif

uniform sampler2D tNormal;
vec3 getViewNormal(const in vec2 screenPosition) {
  #if NORMAL_PACKING == 1
  return unpackRGBAToNormal(texture2D(tNormal, screenPosition));
  #else
  return unpackRGBToNormal(texture2D(tNormal, screenPosition).xyz);
  #endif
}
#define GET_VIEW_NORMAL(viewPosition, screenPosition) getViewNormal(screenPosition)
#else
vec3 getViewNormalFromPosition(const in vec3 viewPosition) {
  return normalize(cross(dFdx(viewPosition), dFdy(viewPosition)));
}
#define GET_VIEW_NORMAL(viewPosition, screenPosition) getViewNormalFromPosition(viewPosition)
#endif

`;
